import { useState } from "react";
import { motion } from "motion/react";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Card } from "@/app/components/ui/card";
import { Separator } from "@/app/components/ui/separator";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import {
  useActionData,
  useNavigation,
  Form,
  useNavigate,
  useSearchParams,
  Link,
} from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { FormActionData } from "@/app/types/form";
import { ROUTES } from "@/app/utils/constants";

export function LoginForm() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const actionData = useActionData<FormActionData>();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleGoogleLogin = () => {
    navigate(ROUTES.AUTH_GOOGLE);
  };

  const oauthError = searchParams.get("error");
  const oauthErrorMessageParam = searchParams.get("message");
  const oauthErrorMessage = oauthErrorMessageParam
    ? decodeURIComponent(oauthErrorMessageParam)
    : null;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80"
        style={{ backgroundImage: 'url("/assets/login-background.png")' }}
      />

      {/* Logo in Upper Left */}
      <motion.div
        className="absolute top-8 left-8 z-20"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 0.6,
          ease: [0.23, 1, 0.32, 1],
          type: "spring",
          damping: 30,
          stiffness: 120,
        }}
      >
        <div className="flex items-center gap-3">
          <motion.div
            className="inline-flex items-center justify-center w-12 h-12"
            whileHover={{ scale: 1.05 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
          >
            <img
              src="/assets/logo.png"
              alt={t("AiAuditBuddy Logo")}
              className="w-12 h-12 rounded-2xl"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              delay: 0.2,
              duration: 0.5,
              ease: "easeOut",
            }}
          >
            <h1
              className="text-[18px] font-semibold text-gray-900"
              style={{
                letterSpacing: "-0.015em",
              }}
            >
              {"AiAuditBuddy"}
            </h1>
          </motion.div>
        </div>
      </motion.div>

      {/* Login Container */}
      <div className="min-h-screen flex items-center justify-center px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.6,
            ease: [0.23, 1, 0.32, 1],
            type: "spring",
            damping: 30,
            stiffness: 120,
          }}
          className="w-full max-w-[480px] "
        >
          <div className="text-center mb-8">
            {/* Welcome Message */}
            <motion.h2
              className="text-[28px] font-semibold text-gray-900"
              style={{
                letterSpacing: "-0.025em",
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.3,
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              {t("Welcome to your audit workspace")}
            </motion.h2>

            <motion.p
              className="text-gray-500 text-[15px]"
              style={{
                letterSpacing: "-0.01em",
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.4,
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              {t("Built for modern teams and smarter audits")}
            </motion.p>
          </div>

          {/* Login Card */}
          <Card className="p-10 border border-transparent bg-white/30 backdrop-blur-md rounded-2xl shadow-none overflow-hidden">
            <div className="absolute inset-0 z-[-1] pointer-events-none">
              <div
                className="absolute inset-0 rounded-2xl border"
                style={{
                  border: "1px solid transparent",
                  borderImage: `linear-gradient(to bottom left, rgba(255,255,255,0.7), rgba(255,255,255,0)) 1`,
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderRadius: "inherit",
                }}
              />
            </div>

            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.5,
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              <Form method="post" className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-[13px] font-medium text-gray-900 block"
                    style={{
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {t("Email")}
                  </label>
                  <Input
                    id="email"
                    name="email"
                    autoComplete="email"
                    type="email"
                    placeholder={t("Enter your email")}
                    className="h-11 bg-white/70 border-1 border-white rounded-lg text-[15px] placeholder:text-gray-400 focus:border-none focus-visible:border-none focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:ring-offset-0 transition-all duration-200 hover:bg-white"
                    required
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="text-[13px] font-medium text-gray-900 block"
                    style={{
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {t("Password")}
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("Enter your password")}
                      className="h-11 bg-white/70 border-1 border-white focus:border-none rounded-lg text-[15px] placeholder:text-gray-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:ring-offset-0 transition-all duration-200 hover:bg-white pr-12"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? t("Hide password") : t("Show password")
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" strokeWidth={1.5} />
                      ) : (
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                      )}
                    </Button>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="mt-2">
                    <Link to={ROUTES.FORGOT_PASSWORD}>
                      <motion.span
                        className="text-[13px] text-gray-500 hover:text-gray-700 transition-colors duration-200 inline-block"
                        whileHover={{ y: -1 }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 25,
                        }}
                      >
                        {t("Forgot your password?")}
                      </motion.span>
                    </Link>
                  </div>
                </div>

                {/* Form Error */}
                {actionData?.message && (
                  <div className="text-red-500 text-sm text-center">
                    {actionData.message}
                  </div>
                )}

                {/* OAuth Error */}
                {oauthError && oauthErrorMessage && (
                  <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg border border-red-200">
                    {oauthErrorMessage}
                  </div>
                )}

                {/* Login Button */}
                <motion.div
                  whileHover={{ scale: 1.0 }}
                  whileTap={{ scale: 0.99 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                  }}
                >
                  <Button
                    type="submit"
                    disabled={navigation.state === "submitting"}
                    className="w-full bg-[#287BFF] text-white hover:bg-[#287BFF]/90 shadow-sm hover:shadow-sm h-11 px-6 gap-2 font-medium rounded-lg transition-all duration-200"
                  >
                    {navigation.state === "submitting" ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t("Logging in...")}</span>
                      </div>
                    ) : (
                      t("Login")
                    )}
                  </Button>
                </motion.div>
              </Form>

              {/* Divider */}
              <div className="relative my-6 flex items-center">
                <Separator className="flex-1" />
                <span className="px-4 text-[13px] text-gray-500">
                  {t("or")}
                </span>
                <Separator className="flex-1" />
              </div>

              {/* Google SSO */}
              <Button
                type="button"
                className="w-full h-11 rounded-lg px-6 gap-3 bg-white border-1 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                onClick={handleGoogleLogin}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="text-base text-gray-800">
                  {t("Continue with Google")}
                </span>
              </Button>

              {/* Footer Links */}
              <div className="text-center pt-4">
                <div className="text-[12px] text-gray-500">
                  {t("Don't have an account?")}{" "}
                  <motion.a
                    href="/signup"
                    className="text-[#287BFF] hover:text-[#287BFF]/80 font-medium transition-colors duration-200"
                    whileHover={{ y: -1 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25,
                    }}
                  >
                    {t("Sign up")}
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
